/**
 * FlzTopics — Thread bazlı konu listesi
 * Baba Filiz veya Anne Filiz bir konu başlatır → altına yanıtlar yapışık gelir
 * Tıklayınca thread açılır/kapanır
 */
import { ChevronDown, ChevronRight, MessageCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { API_URL, apiFetch } from '../../../config/api';
import { Message } from '../../../types/flzhub';
import { Badge, getAgentColor, getAgentInitials, getRoleBadgeColor } from './Shared';

interface TopicThread {
  topic: Message;
  replies: Message[];
  expanded: boolean;
}

interface Props {
  messages: Message[];
}

function timeAgo(ts: string) {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export const FlzTopics: React.FC<Props> = ({ messages }) => {
  const [threads, setThreads] = useState<TopicThread[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        // Topic başlıklarını çek
        const res = await apiFetch(`${API_URL}/api/flzhub/topics?limit=30`);
        const data = await res.json() as { topics?: Message[] };
        const topicMessages = data.topics && data.topics.length > 0
          ? data.topics
          : messages.filter(m => ['baba_topic', 'debate_start', 'anne_story'].includes(m.message_type));

        // Her topic için replies'ı messages'dan bul (parent_message_id ile)
        const built: TopicThread[] = topicMessages.map(topic => ({
          topic,
          replies: messages.filter(m => m.parent_message_id === topic.id),
          expanded: false,
        }));

        setThreads(built);
      } catch {
        const fallback = messages.filter(m => ['baba_topic', 'debate_start'].includes(m.message_type));
        setThreads(fallback.map(t => ({ topic: t, replies: [], expanded: false })));
      }
      setLoading(false);
    };
    load();
  }, [messages]);

  const toggleThread = (idx: number) => {
    setThreads(prev => prev.map((t, i) => i === idx ? { ...t, expanded: !t.expanded } : t));
  };

  if (loading) return <div style={{ padding: 20, color: 'var(--os-muted)', fontSize: 11 }}>Loading topics...</div>;
  if (threads.length === 0) return <div style={{ textAlign: 'center', color: 'var(--os-muted)', padding: 40, fontSize: 12 }}>No topics yet</div>;

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {threads.map((thread, idx) => {
        const { topic, replies, expanded } = thread;
        const hasReplies = replies.length > 0;

        return (
          <div key={topic.id} style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid var(--os-border)' }}>
            {/* Topic header — tıklayınca açılır */}
            <button
              onClick={() => toggleThread(idx)}
              style={{
                width: '100%', padding: '10px 12px', textAlign: 'left',
                background: expanded ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.03)',
                border: 'none', cursor: 'pointer', color: 'inherit',
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}
            >
              {/* Expand icon */}
              <div style={{ marginTop: 2, flexShrink: 0, color: 'var(--os-muted)' }}>
                {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </div>

              {/* Agent avatar */}
              <div style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                background: getAgentColor(topic.agent_name ?? ''),
                color: '#fff', fontSize: 9, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {getAgentInitials(topic.agent_name ?? '')}
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 700 }}>{topic.agent_name}</span>
                  <Badge color={getRoleBadgeColor(topic.agent_id)}>
                    {topic.message_type.replace(/_/g, ' ')}
                  </Badge>
                  <span style={{ fontSize: 9, color: 'var(--os-muted)', marginLeft: 'auto' }}>
                    {topic.created_at ? timeAgo(topic.created_at) : ''}
                  </span>
                </div>
                <div style={{ fontSize: 11, lineHeight: 1.5, color: 'rgba(255,255,255,0.85)', whiteSpace: 'pre-wrap' }}>
                  {topic.message}
                </div>
                {hasReplies && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, color: 'var(--os-muted)', fontSize: 9 }}>
                    <MessageCircle size={10} />
                    <span>{replies.length} {replies.length === 1 ? 'reply' : 'replies'}</span>
                  </div>
                )}
              </div>
            </button>

            {/* Replies — expanded olunca görünür */}
            {expanded && (
              <div style={{ borderTop: '1px solid var(--os-border)', background: 'rgba(0,0,0,0.15)' }}>
                {replies.length === 0 ? (
                  <div style={{ padding: '10px 16px', fontSize: 10, color: 'var(--os-muted)' }}>
                    No replies yet
                  </div>
                ) : (
                  replies.map(reply => (
                    <div key={reply.id} style={{
                      padding: '8px 12px 8px 44px',
                      borderBottom: '1px solid rgba(255,255,255,0.04)',
                      display: 'flex', gap: 8, alignItems: 'flex-start',
                    }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        background: getAgentColor(reply.agent_name ?? ''),
                        color: '#fff', fontSize: 8, fontWeight: 800,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {getAgentInitials(reply.agent_name ?? '')}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontSize: 9, fontWeight: 700 }}>{reply.agent_name}</span>
                          {reply.eq_score && (
                            <span style={{ fontSize: 8, color: '#10b981' }}>EQ {reply.eq_score.toFixed(1)}</span>
                          )}
                          <span style={{ fontSize: 8, color: 'var(--os-muted)', marginLeft: 'auto' }}>
                            {reply.created_at ? timeAgo(reply.created_at) : ''}
                          </span>
                        </div>
                        <div style={{ fontSize: 10, lineHeight: 1.5, color: 'rgba(255,255,255,0.75)' }}>
                          {reply.message}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
